import { createBrowserRouter, Navigate, Outlet, type RouterProviderProps } from 'react-router-dom';
import { DashboardLayout } from './components/layout';
import { DashboardPage, LoginPage, NotFoundPage, BookingsPage, NewBookingPage, BookingDetailPage, CaseDetailPage, CasesPage, RegisterPage } from './pages';
import { InquiriesPage } from './pages/Inquiries';
import { RentalsPage } from './pages/Rentals';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { ResetPasswordPage } from './pages/ResetPassword';
import { StaffManagementPage } from './pages/admin/StaffManagement';
import { AuditLogsPage } from './pages/admin/AuditLogs';
import { SecurityDashboardPage } from './pages/admin/SecurityDashboard';
import { ServiceTypesPage } from './pages/admin/ServiceTypes';
import { BaysPage } from './pages/admin/Bays';
import { BusinessHoursPage } from './pages/admin/BusinessHours';
import { WorkflowConfigPage } from './pages/admin/WorkflowConfig';
import WorkflowEditorPage from './pages/admin/WorkflowEditor';
import { EmailTemplatesPage } from './pages/admin/EmailTemplates';
import CorsOriginsPage from './pages/admin/CorsOrigins';
import SystemUtilitiesPage from './pages/admin/SystemUtilities';
import { WebhooksAdminPage } from './pages/admin/Webhooks';
import SignatureTemplatesPage from './pages/admin/SignatureTemplates';
import { SettingsLayout } from './components/layout/SettingsLayout';
import { HelpLayout } from './components/layout/HelpLayout';
import { ProfilePage } from './pages/settings/Profile';
import { PasswordPage } from './pages/settings/Password';
import { PasskeysPage } from './pages/settings/Passkeys';
import { HelpCenter, GettingStarted, BookingFlow, JobTracking, CustomerPortal, AdminSettings, EmbedWidget } from './pages/help';
import { CustomersPage, CustomerDetailPage } from './pages/customers';
import { VehicleDetailPage } from './pages/vehicles';
import { LeadsDashboard, CampaignDetail } from './pages/marketing';
import { BookingPage } from './pages/public/BookingPage';
import { BookingStatusPage } from './pages/public/BookingStatusPage';
import { SignaturePage } from './pages/public/SignaturePage';
import { MagicLinkVerifyPage } from './pages/portal/MagicLinkVerify';
import { PortalLayout } from './components/layout/PortalLayout';
import { PortalBookingDetailPage } from './pages/portal/PortalBookingDetail';
import { PortalLoginPage } from './pages/portal/PortalLogin';
import { PortalVehiclesPage } from './pages/portal/PortalVehicles';
import { MyBookingsPage } from './pages/customer/MyBookings';
import { EmbedConfigPage } from './pages/EmbedConfig';
import JobBoardPage from './pages/JobBoard';
import { InboxPage } from './pages/Inbox';
import { useAuth } from './contexts/AuthContext';
import { LoadingScreen } from './components/ui';

/**
 * Protected route wrapper
 * Redirects to login if not authenticated
 */
function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

/**
 * Public route wrapper
 * Redirects to dashboard if already authenticated
 */
function PublicRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}



export const router: RouterProviderProps['router'] = createBrowserRouter([
  // Public routes
  {
    element: <PublicRoute />,
    children: [
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/register',
        element: <RegisterPage />,
      },
      {
        path: '/forgot-password',
        element: <ForgotPasswordPage />,
      },
      {
        path: '/reset-password',
        element: <ResetPasswordPage />,
      },
    ],
  },

  // Protected routes
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <DashboardLayout />,
        children: [
          {
            path: '/',
            element: <DashboardPage />,
          },
          {
            path: '/bookings',
            element: <BookingsPage />,
          },
          {
            path: '/bookings/new',
            element: <NewBookingPage />,
          },
          {
            path: '/bookings/:id',
            element: <BookingDetailPage />,
          },
          {
            path: '/jobs',
            element: <JobBoardPage />,
          },
          {
            path: '/cases',
            element: <CasesPage />,
          },
          {
            path: '/cases/:id',
            element: <CaseDetailPage />,
          },
          {
            path: '/customers',
            element: <CustomersPage />,
          },
          {
            path: '/customers/:id',
            element: <CustomerDetailPage />,
          },
          {
            path: '/vehicles',
            element: <Navigate to="/customers?tab=vehicles" replace />,
          },
          {
            path: '/vehicles/:id',
            element: <VehicleDetailPage />,
          },
          {
            path: '/marketing',
            element: <LeadsDashboard />,
          },
          {
            path: '/marketing/campaigns/:id',
            element: <CampaignDetail />,
          },
          {
            path: '/rentals',
            element: <RentalsPage />,
          },
          {
            path: '/inbox',
            element: <InboxPage />,
          },
          {
            path: '/inquiries',
            element: <InquiriesPage />,
          },
          {
            path: '/embed-config',
            element: <EmbedConfigPage />,
          },
          {
            path: '/settings',
            element: <SettingsLayout />,
            children: [
              {
                index: true,
                element: <Navigate to="/settings/profile" replace />,
              },
              {
                path: 'profile',
                element: <ProfilePage />,
              },
              {
                path: 'password',
                element: <PasswordPage />,
              },
              {
                path: 'passkeys',
                element: <PasskeysPage />,
              },
            ],
          },
          {
            path: '/help',
            element: <HelpLayout />,
            children: [
              {
                index: true,
                element: <HelpCenter />,
              },
              {
                path: 'getting-started',
                element: <GettingStarted />,
              },
              {
                path: 'booking-flow',
                element: <BookingFlow />,
              },
              {
                path: 'job-tracking',
                element: <JobTracking />,
              },
              {
                path: 'customer-portal',
                element: <CustomerPortal />,
              },
              {
                path: 'admin-settings',
                element: <AdminSettings />,
              },
              {
                path: 'embed-widget',
                element: <EmbedWidget />,
              },
            ],
          },
          {
            path: '/admin/staff',
            element: <StaffManagementPage />,
          },
          {
            path: '/admin/service-types',
            element: <ServiceTypesPage />,
          },
          {
            path: '/admin/bays',
            element: <BaysPage />,
          },
          {
            path: '/admin/hours',
            element: <BusinessHoursPage />,
          },
          {
            path: '/admin/closed-dates',
            element: <Navigate to="/admin/hours" replace />,
          },
          {
            path: '/admin/booking-rules',
            element: <WorkflowConfigPage />,
          },
          {
            path: '/admin/workflows',
            element: <WorkflowEditorPage />,
          },
          {
            path: '/admin/templates',
            element: <EmailTemplatesPage />,
          },
          {
            path: '/admin/audit',
            element: <AuditLogsPage />,
          },
          {
            path: '/admin/security',
            element: <SecurityDashboardPage />,
          },
          {
            path: '/admin/cors-origins',
            element: <CorsOriginsPage />,
          },
          {
            path: '/admin/webhooks',
            element: <WebhooksAdminPage />,
          },
          {
            path: '/admin/signature-templates',
            element: <SignatureTemplatesPage />,
          },
          {
            path: '/admin/utilities',
            element: <SystemUtilitiesPage />,
          },
          {
            path: '/admin',
            element: <Navigate to="/admin/staff" replace />,
          },
        ],
      },
    ],
  },

  // Public booking routes (no auth required, standalone pages)
  {
    path: '/booking',
    element: <BookingPage />,
  },
  {
    path: '/booking/status',
    element: <BookingStatusPage />,
  },
  {
    path: '/booking/status/:token',
    element: <BookingStatusPage />,
  },
  {
    path: '/portal/verify/:token',
    element: <MagicLinkVerifyPage />,
  },
  {
    path: '/portal/signature/:token',
    element: <SignaturePage />,
  },
  {
    path: '/portal/login',
    element: <PortalLoginPage />,
  },
  {
    path: '/portal',
    element: <PortalLayout />,
    children: [
      { index: true, element: <Navigate to="/portal/bookings" replace /> },
      { path: 'bookings', element: <MyBookingsPage /> },
      { path: 'bookings/:id', element: <PortalBookingDetailPage /> },
      { path: 'vehicles', element: <PortalVehiclesPage /> },
    ],
  },

  // 404
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);
